import { useEffect, useState, type CSSProperties } from "react";
import { useTranslation } from "react-i18next";
import { myShipmentApi, type DispatchNote } from "../../api/dispatchOrders";
import { myLotDocumentApi, type CustomerLotDocument } from "../../api/lotDocuments";
import type { ApiError } from "../../api/client";
import { useToast } from "../Toast";
import { NoteInvoices } from "./NoteInvoices";
import { nazivVrsteDokumenta } from "../../lib/lotDocumentKind";
import { formatDate, formatNumber } from "../../lib/format";

/**
 * What left for the buyer's own order and what was billed for it: the ERP's dispatch notes with the lot of every line,
 * the quality documents released for that lot, and the invoices that bill each note. Read in the buyer's own session;
 * none of it is entered in the portal. Shown once something has left, and nothing before.
 */
export function CustomerOrderShipments({ orderId }: { orderId: string }) {
  const { t, i18n } = useTranslation();
  const toast = useToast();
  const jezik = i18n.resolvedLanguage;
  const [notes, setNotes] = useState<DispatchNote[] | null>(null);
  const [documents, setDocuments] = useState<Map<string, CustomerLotDocument[]>>(new Map());

  useEffect(() => {
    let live = true;
    myShipmentApi.forOrder(orderId)
      .then((rows) => { if (live) setNotes(rows); })
      .catch(() => { if (live) setNotes([]); });
    // Only what quality released comes back; a lot without one simply has no button.
    myLotDocumentApi.forOrder(orderId)
      .then((groups) => { if (live) setDocuments(new Map(groups.map((g) => [g.lot.id, g.documents]))); })
      .catch(() => { if (live) setDocuments(new Map()); });
    return () => { live = false; };
  }, [orderId]);

  async function download(d: CustomerLotDocument) {
    try {
      const { url } = await myLotDocumentApi.downloadUrl(d.id);
      // The signed link is a forced download, so the file does not open in the browser.
      window.open(url, "_blank", "noopener");
    } catch (e) {
      toast.error((e as ApiError).message);
    }
  }

  if (!notes || notes.length === 0) return null;

  return (
    <section style={card} data-testid="customer-shipments">
      <h2 style={title}>{t("orderErp.shipmentsTitle", "Shipments and invoices")}</h2>
      {notes.map((n) => (
        <div key={n.id} style={noteBox} data-testid="customer-note">
          <div style={row}>
            <span>{t("orderErp.note", "Dispatch note {{number}}", { number: n.number })}</span>
            <span style={muted}>{formatDate(n.issueDate)}</span>
          </div>
          {n.lines.map((l, i) => {
            const docs = l.lotId ? documents.get(l.lotId) ?? [] : [];
            return (
              <div key={i} style={lineBox}>
                <div style={row}>
                  <span>{l.productName ?? l.erpProductId ?? "—"}</span>
                  <span style={muted}>{formatNumber(l.quantity, jezik)} {l.unit ?? ""}</span>
                  {l.lot && (
                    <span style={muted}>
                      {t("dispatchOrders.col.lot", "Lot")} <span style={mono}>{l.lot}</span>
                    </span>
                  )}
                </div>
                {docs.length > 0 && (
                  <div style={row}>
                    {docs.map((d) => (
                      <button
                        key={d.id}
                        type="button"
                        className="btn ghost sm"
                        onClick={() => void download(d)}
                        title={d.fileName}
                      >
                        {nazivVrsteDokumenta(d.kind, t)}
                        {d.issueNumber ? ` ${d.issueNumber}` : ""}
                        {d.expired ? ` · ${t("finance.invoices.docExpired", "istekao")}` : ""}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
          <NoteInvoices note={n} />
        </div>
      ))}
    </section>
  );
}

const card: CSSProperties = {
  background: "var(--surface)",
  border: "1px solid var(--border)",
  borderRadius: "var(--r-md)",
  padding: 18,
  display: "flex",
  flexDirection: "column",
  gap: 10,
};
const title: CSSProperties = { margin: 0, fontSize: 14, color: "var(--ink)" };
const noteBox: CSSProperties = {
  display: "flex",
  flexDirection: "column",
  gap: 4,
  paddingLeft: 10,
  borderLeft: "2px solid var(--border)",
};
const lineBox: CSSProperties = { display: "flex", flexDirection: "column", gap: 4 };
const row: CSSProperties = { display: "flex", flexWrap: "wrap", gap: "4px 10px", alignItems: "baseline", fontSize: 13 };
const muted: CSSProperties = { color: "var(--ink-3)", fontSize: 12 };
const mono: CSSProperties = { fontFamily: "var(--mono)", color: "var(--ink)" };
